import { useRef, useCallback, useEffect } from 'react'

interface MiniEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
}

/**
 * Minimal WYSIWYG editor: bold, italic, heading, list.
 * Stores HTML in the data model.
 */
export function MiniEditor({ value, onChange, placeholder }: MiniEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const isInternalChange = useRef(false)

  // Only set innerHTML from outside (initial load or external change)
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value
      }
    }
    isInternalChange.current = false
  }, [value])

  const exec = useCallback((cmd: string, val?: string) => {
    document.execCommand(cmd, false, val)
    editorRef.current?.focus()
    if (editorRef.current) {
      isInternalChange.current = true
      onChange(editorRef.current.innerHTML)
    }
  }, [onChange])

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault()

    const html = e.clipboardData.getData('text/html')
    const plain = e.clipboardData.getData('text/plain')

    let clean: string
    if (html) {
      clean = sanitizeHtml(html)
    } else {
      // Plain text: convert line breaks to <br>
      clean = plain.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')
    }

    document.execCommand('insertHTML', false, clean)

    if (editorRef.current) {
      isInternalChange.current = true
      onChange(editorRef.current.innerHTML)
    }
  }, [onChange])

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true
      onChange(editorRef.current.innerHTML)
    }
  }, [onChange])

  return (
    <div className="rounded-lg border-2 border-surface-lighter bg-surface">
      {/* Toolbar */}
      <div className="flex gap-0.5 border-b border-surface-lighter px-2 py-1">
        <ToolBtn onClick={() => exec('bold')} title="Bold">
          <strong>B</strong>
        </ToolBtn>
        <ToolBtn onClick={() => exec('italic')} title="Italic">
          <em>I</em>
        </ToolBtn>
        <ToolBtn onClick={() => exec('formatBlock', '<h3>')} title="Heading">
          H
        </ToolBtn>
        <ToolBtn onClick={() => exec('insertUnorderedList')} title="List">
          •≡
        </ToolBtn>
      </div>

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        className="mini-editor max-h-[40vh] overflow-y-auto min-h-[80px] px-3 py-2 text-sm text-text focus:outline-none [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-text-muted"
      />
    </div>
  )
}

function ToolBtn({ onClick, title, children }: {
  onClick: () => void
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className="rounded px-2 py-0.5 text-xs text-text-muted hover:bg-surface-lighter hover:text-text"
    >
      {children}
    </button>
  )
}

/** Strip everything except allowed tags: p, br, b, strong, i, em, h3, ul, li */
function sanitizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return cleanNode(doc.body)
}

const ALLOWED_TAGS = new Set(['P', 'BR', 'B', 'STRONG', 'I', 'EM', 'H3', 'UL', 'LI'])

function cleanNode(node: Node): string {
  let result = ''

  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      result += child.textContent ?? ''
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as HTMLElement
      const tag = el.tagName

      if (ALLOWED_TAGS.has(tag)) {
        const lower = tag.toLowerCase()
        if (tag === 'BR') {
          result += '<br>'
        } else {
          result += `<${lower}>${cleanNode(el)}</${lower}>`
        }
      } else if (tag === 'DIV' || tag === 'SPAN' || tag === 'SECTION' || tag === 'ARTICLE') {
        // Unwrap block elements from Word/Google Docs
        const inner = cleanNode(el)
        if (inner.trim()) result += `<p>${inner}</p>`
      } else {
        // Unknown tag: keep text content only
        result += cleanNode(el)
      }
    }
  }

  return result
}
